import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PRIORITY, fmtDate } from "@/lib/format";
import { Field, Sel, opts, mapOpts } from "@/components/Field";
import { FormDialog, num, clean } from "./FormDialog";

const EMPTY = { name: "", description: "", quantity: "1", unit: "", priority: "normal", event_id: "", product_id: "", store: "", budget: "", notes: "" };

export const ShoppingDialog = ({ open, onOpenChange, item, preset, onSaved }) => {
  const { assoc } = useAuth();
  const [f, setF] = useState(EMPTY);
  const [events, setEvents] = useState([]);
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v?.target ? v.target.value : v }));

  useEffect(() => {
    if (!open) return;
    const base = { ...EMPTY, unit: assoc?.units?.[0] || "", ...(preset || {}) };
    setF(item ? { ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, item[k] ?? ""])) } : base);
    api.get("/events?upcoming=true").then((r) => setEvents(r.data)).catch(() => setEvents([]));
  }, [open, item, preset, assoc]);

  const send = async (force) => {
    const body = clean({ ...f, quantity: num(f.quantity), budget: num(f.budget) });
    if (item) {
      delete body.event_id;
      delete body.product_id;
      return api.patch(`/shopping/${item.id}`, body);
    }
    return api.post("/shopping", { ...body, force });
  };

  const submit = async () => {
    setSaving(true);
    try {
      await send(false);
      toast.success(item ? "Richiesta aggiornata" : "Aggiunto alla lista della spesa");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      if (e.response?.status === 409 && !item) {
        toast.warning(formatError(e), {
          duration: 10000,
          action: { label: "Aggiungi comunque", onClick: () => send(true).then(() => { toast.success("Aggiunto"); onOpenChange(false); onSaved?.(); }).catch((x) => toast.error(formatError(x))) },
        });
      } else toast.error(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormDialog open={open} onOpenChange={onOpenChange} title={item ? "Modifica richiesta" : "Aggiungi alla spesa"} onSubmit={submit} saving={saving} testId="shopping-dialog" submitLabel={item ? "Salva" : "Aggiungi"}>
      <Field label="Articolo *"><Input data-testid="shopping-name-input" required value={f.name} onChange={set("name")} className="h-11" /></Field>
      <div className="grid grid-cols-3 gap-3">
        <Field label="Quantità *"><Input data-testid="shopping-quantity-input" type="number" min="0" step="any" required value={f.quantity} onChange={set("quantity")} className="h-11" /></Field>
        <Field label="Unità"><Sel testId="shopping-unit-select" value={f.unit} onChange={set("unit")} options={opts(assoc?.units)} /></Field>
        <Field label="Priorità"><Sel testId="shopping-priority-select" value={f.priority} onChange={set("priority")} options={mapOpts(PRIORITY)} /></Field>
      </div>
      {!item && events.length > 0 && (
        <Field label="Evento collegato"><Sel testId="shopping-event-select" value={f.event_id} onChange={set("event_id")} placeholder="Nessuno" options={events.map((e) => ({ value: e.id, label: `${e.name} (${fmtDate(e.start)})` }))} /></Field>
      )}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Negozio suggerito"><Input data-testid="shopping-store-input" value={f.store} onChange={set("store")} className="h-11" /></Field>
        <Field label="Budget indicativo (CHF)"><Input data-testid="shopping-budget-input" type="number" min="0" step="0.05" value={f.budget} onChange={set("budget")} className="h-11" /></Field>
      </div>
      <Field label="Descrizione / note"><Textarea data-testid="shopping-notes-input" value={f.notes} onChange={set("notes")} rows={2} /></Field>
    </FormDialog>
  );
};
